import { FC } from 'react';
import dayjs from 'dayjs';
import { Button, Checkbox } from 'antd';
import {
  CreditCardOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';

import {
  GROUP_STATE_LABEL,
  PAYMENT_METHODS,
  PaymentMethod,
  TrainingGroup,
} from '../CourseEnrollment.const';
import css from '../CourseEnrollment.module.css';

interface Props {
  courseTitle: string;
  contractNumber: string;
  listenersCount: number;
  pricePerStudent: string;
  totalPrice: string;
  groupId: string;
  onGroupChange: (id: string) => void;
  consent: boolean;
  onConsentChange: (v: boolean) => void;
  paymentMethod: PaymentMethod;
  onPaymentMethodChange: (m: PaymentMethod) => void;
  onPay: () => void;
  onViewContract: () => void;
  groups: TrainingGroup[];
}

const paymentIcon = (m: PaymentMethod) => {
  if (m === 'erip') return <QrcodeOutlined />;
  if (m === 'card') return <CreditCardOutlined />;
  return <FileTextOutlined />;
};

const GroupAndPaymentStep: FC<Props> = ({
  courseTitle,
  contractNumber,
  listenersCount,
  pricePerStudent,
  totalPrice,
  groupId,
  onGroupChange,
  consent,
  onConsentChange,
  paymentMethod,
  onPaymentMethodChange,
  onPay,
  onViewContract,
  groups,
}) => {
  const selectedGroup = groups.find((g) => g.id === groupId);
  const paymentDeadline = selectedGroup
    ? (() => {
        const [d, m, y] = selectedGroup.startDate.split('.');
        return dayjs(`${y}-${m}-${d}`).subtract(1, 'day').format('DD.MM.YYYY');
      })()
    : null;

  return (
  <>
    <div className={css.TopSummary}>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Курс</span>
        <span className={css.TopSummaryValue}>{courseTitle}</span>
      </div>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Номер договора</span>
        <span className={css.TopSummaryValue}>{contractNumber}</span>
      </div>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Слушателей</span>
        <span className={css.TopSummaryValue}>{listenersCount} человек</span>
      </div>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Цена за слушателя</span>
        <span className={css.TopSummaryValue}>{pricePerStudent}</span>
      </div>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Итого к оплате</span>
        <span className={css.TopSummaryValue}>{totalPrice}</span>
      </div>
    </div>

    <h3 className={css.SectionLabel}>Выберите группу обучения</h3>
    {groups.length === 0 ? (
      <p>Группы для этого курса пока не назначены</p>
    ) : (
      <div className={css.GroupGrid}>
        {groups.map((g) => {
          const isActive = g.id === groupId;
          return (
            <div
              key={g.id}
              className={`${css.GroupCard} ${
                isActive ? css.GroupCardActive : ''
              }`}
              onClick={() => onGroupChange(g.id)}
            >
              <div className={css.GroupCardHead}>
                <span
                  className={`${css.GroupRadio} ${
                    isActive ? css.GroupRadioActive : ''
                  }`}
                />
                <span className={css.GroupCardName}>{g.name}</span>
              </div>
              <div className={css.GroupField}>
                <span className={css.GroupFieldLabel}>Начало</span>
                <span className={css.GroupFieldValue}>{g.startDate}</span>
              </div>
              <div className={css.GroupField}>
                <span className={css.GroupFieldLabel}>Окончание</span>
                <span className={css.GroupFieldValue}>{g.endDate}</span>
              </div>
              <div className={css.GroupCardDivider} />
              <div className={css.GroupField}>
                <span className={css.GroupFieldLabel}>Состояние</span>
                <span className={css.GroupStateBadge}>
                  {GROUP_STATE_LABEL[g.state]}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    )}

    <div className={css.PaymentNotice}>
      <span className={css.NoticeIcon}>
        <InfoCircleOutlined />
      </span>
      <span>
        Срок оплаты — не позднее дня, предшествующего дню начала следующей
        группы{paymentDeadline ? ` (${paymentDeadline})` : ''}.
      </span>
    </div>

    <Checkbox
      className={css.Consent}
      checked={consent}
      onChange={(e) => onConsentChange(e.target.checked)}
    >
      Я подтверждаю, что все перечисленные слушатели согласны на обработку
      персональных данных в соответствии с{' '}
      <a className={css.ConsentLink} href='#privacy'>
        Политикой обработки персональных данных
      </a>
      .
    </Checkbox>

    <div className={css.PrimaryActions}>
      <Button className={css.ViewContractBtn} onClick={onViewContract}>Посмотреть договор</Button>
      <Button className={css.PayBtn} disabled={!consent} onClick={onPay}>
        Оплатить
      </Button>
    </div>

    <h3 className={css.PaymentMethodsLabel}>Выберите способ оплаты</h3>
    <div className={css.PaymentMethodsGrid}>
      {PAYMENT_METHODS.map((m) => (
        <button
          type='button'
          key={m.value}
          className={`${css.PaymentMethodCard} ${
            paymentMethod === m.value ? css.PaymentMethodCardActive : ''
          }`}
          onClick={() => onPaymentMethodChange(m.value)}
        >
          <span className={css.PaymentMethodIcon}>{paymentIcon(m.value)}</span>
          {m.label}
        </button>
      ))}
    </div>
  </>
  );
};

export default GroupAndPaymentStep;
